import React from "react";
import { Check, Incident } from "../../models/Incidents";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import {
  setCurrentIndex,
  setShowModal,
} from "../../store/slices/incidenciaSlice.ts";
import dayjs from "dayjs";

const ModalClock = ({
  close,
  records,
  updateCheckout,
}: {
  close: () => void;
  records: Incident[];
  updateCheckout: (check: Check, index: number) => void;
}) => {
  const dispatch = useDispatch();
  const { currentIndex } = useSelector((state: RootState) => state.incidencias);
  const record = records[currentIndex];
  const [error, setError] = React.useState<string | boolean>(false);

  const getInitialTime = () => {
    if (record?.checkOut?.checkTime) {
      const timeParts = record.checkOut.checkTime.split(":");
      return {
        hours: parseInt(timeParts[0]) || 12,
        minutes: parseInt(timeParts[1]) || 0,
      };
    }
    return { hours: 12, minutes: 0 };
  };

  const initialTime = getInitialTime();
  const [hours, setHours] = React.useState(initialTime.hours);
  const [minutes, setMinutes] = React.useState(initialTime.minutes);

  const [editingMode, setEditingMode] = React.useState<"hours" | "minutes">(
    "hours"
  );

  const [hoursAnimation, setHoursAnimation] = React.useState(false);
  const [minutesAnimation, setMinutesAnimation] = React.useState(false);

  const [viewMode, setViewMode] = React.useState<"buttons" | "clock">(
    "buttons"
  );

  const hoursRef = React.useRef<HTMLDivElement>(null);
  const minutesRef = React.useRef<HTMLDivElement>(null);
  const clockRef = React.useRef<HTMLDivElement>(null);

  const hoursScrollAccumulator = React.useRef(0);
  const minutesScrollAccumulator = React.useRef(0);

  const SCROLL_THRESHOLD = 80;

  const incrementHours = () => {
    setHoursAnimation(true);
    setHours((prev) => (prev + 1) % 24);
    setError(false);
    setTimeout(() => setHoursAnimation(false), 300);
  };

  const decrementHours = () => {
    setHoursAnimation(true);
    setHours((prev) => (prev - 1 + 24) % 24);
    setError(false);
    setTimeout(() => setHoursAnimation(false), 300);
  };

  const incrementMinutes = () => {
    setMinutesAnimation(true);
    setMinutes((prev) => (prev + 1) % 60);
    setError(false);
    setTimeout(() => setMinutesAnimation(false), 300);
  };

  const decrementMinutes = () => {
    setMinutesAnimation(true);
    setMinutes((prev) => (prev - 1 + 60) % 60);
    setError(false);
    setTimeout(() => setMinutesAnimation(false), 300);
  };

  React.useEffect(() => {
    const hoursElement = hoursRef.current;
    const minutesElement = minutesRef.current;

    const handleHoursScroll = (e: WheelEvent) => {
      e.preventDefault();
      hoursScrollAccumulator.current += e.deltaY;

      if (Math.abs(hoursScrollAccumulator.current) >= SCROLL_THRESHOLD) {
        if (hoursScrollAccumulator.current > 0) {
          decrementHours();
        } else {
          incrementHours();
        }
        hoursScrollAccumulator.current = 0;
      }
    };

    const handleMinutesScroll = (e: WheelEvent) => {
      e.preventDefault();
      minutesScrollAccumulator.current += e.deltaY;

      if (Math.abs(minutesScrollAccumulator.current) >= SCROLL_THRESHOLD) {
        if (minutesScrollAccumulator.current > 0) {
          decrementMinutes();
        } else {
          incrementMinutes();
        }
        minutesScrollAccumulator.current = 0;
      }
    };

    if (hoursElement) {
      hoursElement.addEventListener("wheel", handleHoursScroll, {
        passive: false,
      });
    }
    if (minutesElement) {
      minutesElement.addEventListener("wheel", handleMinutesScroll, {
        passive: false,
      });
    }

    return () => {
      if (hoursElement) {
        hoursElement.removeEventListener("wheel", handleHoursScroll);
      }
      if (minutesElement) {
        minutesElement.removeEventListener("wheel", handleMinutesScroll);
      }
    };
  }, []);

  const handleClockClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (viewMode !== "clock") return;

    const clock = clockRef.current;
    if (!clock) return;

    const rect = clock.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const angle = Math.atan2(e.clientY - centerY, e.clientX - centerX);

    let degrees = (angle * 180) / Math.PI;

    degrees = degrees + 90;

    if (degrees < 0) degrees += 360;
    if (degrees >= 360) degrees -= 360;

    if (editingMode === "hours") {
      let hour12 = Math.round(degrees / 30);
      if (hour12 === 0 || hour12 === 12) hour12 = 12;
      if (hour12 > 12) hour12 = hour12 % 12;
      if (hour12 === 0) hour12 = 12;

      const isPM = degrees > 180 && degrees <= 360;

      let finalHour = hour12;
      if (isPM && hour12 !== 12) {
        finalHour = hour12 + 12;
      } else if (!isPM && hour12 === 12) {
        finalHour = 0;
      }

      setHours(finalHour);
    } else {
      let minute = Math.round(degrees / 6);
      if (minute === 60) minute = 0;
      if (minute < 0) minute = 0;
      if (minute >= 60) minute = 59;
      setMinutes(minute);
    }

    setError(false);
  };

  const handleAccept = () => {
    const { checkDate } = record.checkIn;
    let baseDate = dayjs(checkDate);

    const hoursStr = hours.toString().padStart(2, "0");
    const minutesStr = minutes.toString().padStart(2, "0");

    const checkTime = dayjs(record.checkIn.checkTime, "HH:mm:ss");
    const selectedTime = dayjs(`${hoursStr}:${minutesStr}:00`, "HH:mm:ss");
    const lowerLimit = dayjs("02:00:00", "HH:mm:ss");

    const isBetween =
      selectedTime.isAfter(lowerLimit) && selectedTime.isBefore(checkTime);

    if (isBetween) {
      return setError(
        "La hora de salida no puede ser menor a la hora de entrada"
      );
    }

    const checkInTime = `${hoursStr}:${minutesStr}:00`;

    if (checkInTime >= "00:00:00" && checkInTime <= "02:00:00") {
      baseDate = baseDate.add(1, "day");
    }

    const checkOut = {
      checkTime: checkInTime,
      checkDate: baseDate.format("YYYY-MM-DD"),
      id: -99,
    };

    updateCheckout(checkOut, currentIndex);
    dispatch(setShowModal(false));
    dispatch(setCurrentIndex(-1));
  };

  const displayHour = hours % 12 === 0 ? 12 : hours % 12;

  let hourAngle = displayHour * 30 + minutes * 0.5 - 90;
  if (hourAngle < 0) hourAngle += 360;

  let minuteAngle = minutes * 6 - 90;
  if (minuteAngle < 0) minuteAngle += 360;

  return (
    <div className="col-md-12">
      {error && (
        <div
          className="alert alert-danger d-flex align-items-center mb-3"
          role="alert"
          style={{
            borderRadius: "8px",
            fontSize: "13px",
          }}
        >
          <i className="fa-solid fa-circle-exclamation fa-lg me-2"></i>
          <strong>{error}</strong>
        </div>
      )}

      <div
        className="d-flex flex-column align-items-center"
        style={{
          padding: "40px 20px",
          background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
          borderRadius: "16px",
          boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
          position: "relative",
        }}
      >
        <button
          onClick={() =>
            setViewMode((prev) => (prev === "buttons" ? "clock" : "buttons"))
          }
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            background: "white",
            border: "2px solid #50B7C0",
            borderRadius: "8px",
            padding: "8px 16px",
            fontSize: "13px",
            fontWeight: "600",
            color: "#50B7C0",
            cursor: "pointer",
            transition: "all 0.3s ease",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#50B7C0";
            e.currentTarget.style.color = "white";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "white";
            e.currentTarget.style.color = "#50B7C0";
          }}
        >
          <i
            className={`fa fa-${viewMode === "buttons" ? "clock" : "list"}`}
          ></i>
          {viewMode === "buttons" ? "Reloj" : "Botones"}
        </button>

        <div className="mb-3 text-center">
          <h5
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: "#495057",
              marginBottom: "8px",
            }}
          >
            Seleccionar Hora de Salida
          </h5>
          <div
            style={{
              fontSize: "13px",
              color: "#6c757d",
              fontWeight: "500",
            }}
          >
            Editando:{" "}
            <span style={{ color: "#50B7C0", fontWeight: "600" }}>
              {editingMode === "hours" ? "Horas" : "Minutos"}
            </span>
          </div>
        </div>

        {viewMode === "clock" && (
          <div className="d-flex gap-2 mb-4 align-items-center">
            <div
              onClick={() => setEditingMode("hours")}
              style={{
                fontSize: "64px",
                fontWeight: "700",
                color: editingMode === "hours" ? "#50B7C0" : "#dee2e6",
                fontFamily: "monospace",
                cursor: "pointer",
                transition: "all 0.3s ease",
                padding: "8px 16px",
                borderRadius: "12px",
                background:
                  editingMode === "hours"
                    ? "rgba(80, 183, 192, 0.1)"
                    : "transparent",
                border:
                  editingMode === "hours"
                    ? "3px solid #50B7C0"
                    : "3px solid transparent",
                position: "relative",
              }}
              onMouseEnter={(e) => {
                if (editingMode !== "hours") {
                  e.currentTarget.style.color = "#50B7C0";
                }
              }}
              onMouseLeave={(e) => {
                if (editingMode !== "hours") {
                  e.currentTarget.style.color = "#dee2e6";
                }
              }}
            >
              {hours.toString().padStart(2, "0")}
              <span
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: editingMode === "hours" ? "#50B7C0" : "#adb5bd",
                  position: "absolute",
                  top: "8px",
                  right: "8px",
                }}
              >
                {hours >= 12 ? "PM" : "AM"}
              </span>
            </div>
            <div
              style={{
                fontSize: "64px",
                fontWeight: "700",
                color: "#50B7C0",
                fontFamily: "monospace",
              }}
            >
              :
            </div>
            <div
              onClick={() => setEditingMode("minutes")}
              style={{
                fontSize: "64px",
                fontWeight: "700",
                color: editingMode === "minutes" ? "#50B7C0" : "#dee2e6",
                fontFamily: "monospace",
                cursor: "pointer",
                transition: "all 0.3s ease",
                padding: "8px 16px",
                borderRadius: "12px",
                background:
                  editingMode === "minutes"
                    ? "rgba(80, 183, 192, 0.1)"
                    : "transparent",
                border:
                  editingMode === "minutes"
                    ? "3px solid #50B7C0"
                    : "3px solid transparent",
              }}
              onMouseEnter={(e) => {
                if (editingMode !== "minutes") {
                  e.currentTarget.style.color = "#50B7C0";
                }
              }}
              onMouseLeave={(e) => {
                if (editingMode !== "minutes") {
                  e.currentTarget.style.color = "#dee2e6";
                }
              }}
            >
              {minutes.toString().padStart(2, "0")}
            </div>
          </div>
        )}

        {viewMode === "buttons" ? (
          /* Vista de Botones - Muestra ambos (horas y minutos) con scroll */
          <div className="d-flex gap-4 align-items-center justify-content-center mb-4">
            {/* Horas */}
            <div className="d-flex flex-column align-items-center">
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#6c757d",
                  marginBottom: "8px",
                  textTransform: "uppercase",
                }}
              >
                Horas
              </div>
              <button
                onClick={incrementHours}
                style={{
                  width: "100px",
                  height: "36px",
                  background:
                    "linear-gradient(135deg, #50B7C0 0%, #3A9BA3 100%)",
                  border: "none",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "18px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  marginBottom: "10px",
                  boxShadow: "0 2px 6px rgba(80, 183, 192, 0.25)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.03)";
                  e.currentTarget.style.boxShadow =
                    "0 3px 8px rgba(80, 183, 192, 0.35)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow =
                    "0 2px 6px rgba(80, 183, 192, 0.25)";
                }}
              >
                <i className="fa fa-chevron-up"></i>
              </button>

              <div
                ref={hoursRef}
                style={{
                  width: "120px",
                  height: "120px",
                  background: "white",
                  borderRadius: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "56px",
                  fontWeight: "700",
                  color: "#50B7C0",
                  fontFamily: "monospace",
                  boxShadow: "0 6px 16px rgba(80, 183, 192, 0.15)",
                  marginBottom: "10px",
                  cursor: "ns-resize",
                  userSelect: "none",
                  transition: hoursAnimation
                    ? "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)"
                    : "all 0.2s ease",
                  transform: hoursAnimation ? "scale(1.08)" : "scale(1)",
                  border: "3px solid #50B7C0",
                }}
              >
                {hours.toString().padStart(2, "0")}
              </div>

              <button
                onClick={decrementHours}
                style={{
                  width: "100px",
                  height: "36px",
                  background:
                    "linear-gradient(135deg, #50B7C0 0%, #3A9BA3 100%)",
                  border: "none",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "18px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  boxShadow: "0 2px 6px rgba(80, 183, 192, 0.25)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.03)";
                  e.currentTarget.style.boxShadow =
                    "0 3px 8px rgba(80, 183, 192, 0.35)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow =
                    "0 2px 6px rgba(80, 183, 192, 0.25)";
                }}
              >
                <i className="fa fa-chevron-down"></i>
              </button>
            </div>

            {/* Separador */}
            <div
              style={{
                fontSize: "48px",
                fontWeight: "700",
                color: "#50B7C0",
                fontFamily: "monospace",
                marginTop: "40px",
              }}
            >
              :
            </div>

            {/* Minutos */}
            <div className="d-flex flex-column align-items-center">
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#6c757d",
                  marginBottom: "8px",
                  textTransform: "uppercase",
                }}
              >
                Minutos
              </div>
              <button
                onClick={incrementMinutes}
                style={{
                  width: "100px",
                  height: "36px",
                  background:
                    "linear-gradient(135deg, #50B7C0 0%, #3A9BA3 100%)",
                  border: "none",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "18px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  marginBottom: "10px",
                  boxShadow: "0 2px 6px rgba(80, 183, 192, 0.25)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.03)";
                  e.currentTarget.style.boxShadow =
                    "0 3px 8px rgba(80, 183, 192, 0.35)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow =
                    "0 2px 6px rgba(80, 183, 192, 0.25)";
                }}
              >
                <i className="fa fa-chevron-up"></i>
              </button>

              <div
                ref={minutesRef}
                style={{
                  width: "120px",
                  height: "120px",
                  background: "white",
                  borderRadius: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "56px",
                  fontWeight: "700",
                  color: "#50B7C0",
                  fontFamily: "monospace",
                  boxShadow: "0 6px 16px rgba(80, 183, 192, 0.15)",
                  marginBottom: "10px",
                  cursor: "ns-resize",
                  userSelect: "none",
                  transition: minutesAnimation
                    ? "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)"
                    : "all 0.2s ease",
                  transform: minutesAnimation ? "scale(1.08)" : "scale(1)",
                  border: "3px solid #50B7C0",
                }}
              >
                {minutes.toString().padStart(2, "0")}
              </div>

              <button
                onClick={decrementMinutes}
                style={{
                  width: "100px",
                  height: "36px",
                  background:
                    "linear-gradient(135deg, #50B7C0 0%, #3A9BA3 100%)",
                  border: "none",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "18px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  boxShadow: "0 2px 6px rgba(80, 183, 192, 0.25)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.03)";
                  e.currentTarget.style.boxShadow =
                    "0 3px 8px rgba(80, 183, 192, 0.35)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow =
                    "0 2px 6px rgba(80, 183, 192, 0.25)";
                }}
              >
                <i className="fa fa-chevron-down"></i>
              </button>
            </div>
          </div>
        ) : (
          /* Vista de Reloj Analógico Mejorado */
          <div className="d-flex flex-column align-items-center mb-4">
            <div
              style={{
                fontSize: "14px",
                color: "#6c757d",
                marginBottom: "16px",
                fontWeight: "500",
                textAlign: "center",
              }}
            >
              {editingMode === "hours"
                ? "Click o arrastra en el reloj para seleccionar la hora"
                : "Click o arrastra en el reloj para seleccionar los minutos"}
            </div>

            <div
              ref={clockRef}
              onClick={handleClockClick}
              onMouseMove={(e) => {
                if (e.buttons === 1) handleClockClick(e);
              }}
              style={{
                width: "320px",
                height: "320px",
                borderRadius: "50%",
                background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                boxShadow:
                  "0 12px 32px rgba(0,0,0,0.15), inset 0 2px 8px rgba(0,0,0,0.05)",
                position: "relative",
                cursor: "pointer",
                border: "10px solid #50B7C0",
              }}
            >
              {/* Marcadores de horas (1-12) o minutos (0, 5, 10, ...) */}
              {editingMode === "hours"
                ? [...Array(12)].map((_, i) => {
                    const hourValue = i + 1;
                    const angle = hourValue * 30 - 90;
                    const x = 50 + 42 * Math.cos((angle * Math.PI) / 180);
                    const y = 50 + 42 * Math.sin((angle * Math.PI) / 180);
                    const isSelected =
                      hours % 12 === hourValue ||
                      (hours % 12 === 0 && hourValue === 12);
                    return (
                      <React.Fragment key={i}>
                        {/* Marcador grande en la posición */}
                        <div
                          style={{
                            position: "absolute",
                            left: `${
                              50 + 35 * Math.cos((angle * Math.PI) / 180)
                            }%`,
                            top: `${
                              50 + 35 * Math.sin((angle * Math.PI) / 180)
                            }%`,
                            transform: "translate(-50%, -50%)",
                            width: "4px",
                            height: "16px",
                            background: isSelected ? "#50B7C0" : "#dee2e6",
                            borderRadius: "2px",
                          }}
                        />
                        {/* Número de la hora */}
                        <div
                          style={{
                            position: "absolute",
                            left: `${x}%`,
                            top: `${y}%`,
                            transform: "translate(-50%, -50%)",
                            fontSize: "18px",
                            fontWeight: "700",
                            width: "32px",
                            height: "32px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "50%",
                            background: isSelected ? "#50B7C0" : "transparent",
                            color: isSelected ? "white" : "#495057",
                            transition: "all 0.2s ease",
                            boxShadow: isSelected
                              ? "0 2px 8px rgba(80, 183, 192, 0.4)"
                              : "none",
                          }}
                        >
                          {hourValue}
                        </div>
                      </React.Fragment>
                    );
                  })
                : [...Array(60)].map((_, i) => {
                    const minuteValue = i;
                    const angle = minuteValue * 6 - 90;
                    const isMainMarker = minuteValue % 5 === 0;
                    const isSelected = minutes === minuteValue;
                    const x =
                      50 +
                      (isMainMarker ? 42 : 45) *
                        Math.cos((angle * Math.PI) / 180);
                    const y =
                      50 +
                      (isMainMarker ? 42 : 45) *
                        Math.sin((angle * Math.PI) / 180);

                    return (
                      <React.Fragment key={i}>
                        {/* Marcador pequeño para cada minuto */}
                        <div
                          style={{
                            position: "absolute",
                            left: `${
                              50 + 38 * Math.cos((angle * Math.PI) / 180)
                            }%`,
                            top: `${
                              50 + 38 * Math.sin((angle * Math.PI) / 180)
                            }%`,
                            transform: "translate(-50%, -50%)",
                            width: isMainMarker ? "3px" : "1.5px",
                            height: isMainMarker ? "12px" : "6px",
                            background: isSelected
                              ? "#50B7C0"
                              : isMainMarker
                              ? "#adb5bd"
                              : "#dee2e6",
                            borderRadius: "1px",
                          }}
                        />
                        {/* Número solo para marcadores principales */}
                        {isMainMarker && (
                          <div
                            style={{
                              position: "absolute",
                              left: `${x}%`,
                              top: `${y}%`,
                              transform: "translate(-50%, -50%)",
                              fontSize: "14px",
                              fontWeight: "600",
                              color: isSelected ? "#50B7C0" : "#6c757d",
                              background: isSelected
                                ? "rgba(80, 183, 192, 0.1)"
                                : "transparent",
                              width: "24px",
                              height: "24px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: "50%",
                            }}
                          >
                            {minuteValue.toString().padStart(2, "0")}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}

              {/* Centro del reloj */}
              <div
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  width: "16px",
                  height: "16px",
                  borderRadius: "50%",
                  background: "#50B7C0",
                  border: "3px solid white",
                  transform: "translate(-50%, -50%)",
                  zIndex: 15,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                }}
              />
            </div>
          </div>
        )}

        <div className="d-flex gap-3 w-100 justify-content-center mt-3">
          <button
            className="btn"
            onClick={() => {
              dispatch(setShowModal(false));
              dispatch(setCurrentIndex(-1));
            }}
            style={{
              background: "white",
              color: "#6c757d",
              border: "2px solid #dee2e6",
              borderRadius: "10px",
              padding: "12px 32px",
              fontSize: "14px",
              fontWeight: "600",
              transition: "all 0.3s ease",
              minWidth: "140px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#6c757d";
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow =
                "0 4px 12px rgba(108, 117, 125, 0.2)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#dee2e6";
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            Cancelar
          </button>
          <button
            className="btn"
            onClick={handleAccept}
            style={{
              background: "linear-gradient(135deg, #50B7C0 0%, #3A9BA3 100%)",
              color: "white",
              border: "none",
              borderRadius: "10px",
              padding: "12px 32px",
              fontSize: "14px",
              fontWeight: "600",
              transition: "all 0.3s ease",
              minWidth: "140px",
              boxShadow: "0 4px 12px rgba(80, 183, 192, 0.3)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow =
                "0 6px 16px rgba(80, 183, 192, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                "0 4px 12px rgba(80, 183, 192, 0.3)";
            }}
          >
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalClock;
